import { IEpticaExctractEvent } from '@commons/history/history-mail/models/eptica-extract';
import { ChannelUtil } from '@commons/util/helper/channel';
import { optToPromise } from '@commons/util/utilities/option';
import { RxUtil } from '@commons/util/utilities/rx';
import { Actions, ofType } from '@ngrx/effects';
import { IPromise, IQService } from 'angular';
import { delay, map, mergeMap, Observable, of, timer, withLatestFrom } from 'rxjs';

import {
  Booking,
  BookingAction,
  BookingFacade,
  BookingMailService,
  BookingService,
  BookingStatus,
  Channel,
  ChannelDirection,
  ChannelType,
  Customer,
  CustomerAddress,
  deepCopy,
  initialBooking,
  isNotNullOrUndefined,
  Option,
  Qualification,
  RefuseReason,
  Reschedule,
  TimeSlot,
  TimeSlotSelect,
} from '@contact/upgrade';

import { IContactStatusDeprecatedService } from './contact-status-deprecated.service';

const MODULE_NAME = 'contactDeprecatedService';

/**
 * @deprecated
 */
export interface IContactStateEvent {
  contact: Booking;
  state: BookingStatus;
  previousState: BookingStatus | undefined;
}

/**
 * @deprecated prefer BookingFacade
 */
export interface IContactDeprecatedService {
  /**
   * @deprecated, prefer initialBooking constant
   *
   * Build a fake contact object the the only id
   */
  buildFakeContact(id: string): Booking;

  /**
   * @deprecated, prefer BookingFacade.getContact
   *
   * Get contact corresponding to given response identifier
   * @param {string} responseId the response identifier
   */
  getContact(responseId: string): IPromise<Booking>;

  /**
   * @deprecated, prefer BookingFacade.getQualifById
   *
   * Get qualification corresponding to given response identifier
   * @param {string} responseId the response identifier
   */
  getQualif(responseId: string): IPromise<Qualification | undefined>;

  /**
   * @deprecated, prefer BookingFacade.getCustomer
   *
   * Get customer corresponding to given response identifier
   * @param {string} responseId the response identifier
   */
  getCustomer(responseId: string): IPromise<Customer | undefined>;

  /**
   * @deprecated, prefer BookingFacade.getCustomerAddresses
   *
   * Get customer addresses corresponding to given response identifier
   * @param {string} responseId the response identifier
   */
  getCustomerAddresses(responseId: string): IPromise<CustomerAddress[]>;

  /**
   * @deprecated, prefer BookingFacade.contacts$
   *
   * Get all contacts
   */
  getContacts(): Booking[];

  /**
   * @deprecated, prefer BookingFacade.contacts$
   *
   * Get contacts array corresponding to given channel
   * @param {IChannel} channel the channel to which looking for contacts
   */
  getContactsByChannel(channel: Channel): Booking[];

  /**
   * @deprecated, prefer BookingFacade.contacts$
   *
   * Get contacts array corresponding to given channel type
   * @param {ChannelType} channelType the channel type to which looking for contacts
   */
  getContactsByChannelType(channelType: ChannelType): Booking[];

  /**
   * @deprecated, prefer BookingFacade.contacts$
   *
   * Get all Channel for which there are an active Booking
   */
  getChannelWithActiveContactsByChannelType(channelType: ChannelType): Channel[];

  /**
   * @deprecated, prefer BookingFacade.getTransitionById
   *
   * Get contact state corresponding to given response identifier
   * @param {string} responseId the contact response identifier
   */
  getContactState(responseId: string): BookingStatus;

  /**
   * @deprecated, prefer BookingFacade.select
   *
   * Select contact corresponding to given response identifier if exists
   * @param {string} responseId the response identifier
   */
  selectContact(responseId: string): void;

  /**
   * @deprecated, prefer BookingFacade.selected$
   *
   * Get currently selected contact if exists
   */
  getSelectedContact(): IPromise<Booking>;

  /**
   * @deprecated, prefer BookingFacade.selectedQualification$
   *
   * Get currently selected qualification if exists
   */
  getSelectedQualif(): IPromise<Qualification | undefined>;

  /**
   * @deprecated, prefer BookingFacade.selectedId$
   *
   * Return true if given response identifier is corresponding to currently selected contact, false else
   * @param {string} responseId the response identifier
   */
  isContactSelected(responseId: string): boolean;

  /**
   * @deprecated, prefer BookingFacade.update
   *
   * update context for contact of given responseId if exists
   */
  updateContext(responseId: string, newContext: { [key: string]: string }): IPromise<Booking>;

  /**
   * @deprecated, prefer BookingFacade.update
   *
   * update response attribute of contact to set values from mail bounce comment information
   * @param {string} responseId
   * @param {IEpticaExctractEvent} evt
   */
  updateTransferredMailState(responseId: string, evt: IEpticaExctractEvent): IPromise<Booking>;

  /**
   * @deprecated, prefer BookingFacade.update
   */
  updateReschedule(
    responseId: string,
    reschedule: Reschedule,
    selectedDayTimeSlot: Option<TimeSlotSelect>,
    selectedTimeSlot: Option<TimeSlot>
  ): IPromise<Booking>;

  /**
   * @deprecated, prefer BookingFacade.update
   */
  updateRequestId(responseId: string, epticaRequestId: number): IPromise<Booking>;

  /**
   * @deprecated, prefer BookingFacade.accept
   *
   * Accept the response (contact)
   * @param {string} responseId the response identifier
   */
  accept(responseId: string): IPromise<Booking>;

  /**
   * @deprecated, prefer BookingFacade.process
   *
   * Process the response (contact)
   * @param {string} responseId the response identifier
   */
  process(responseId: string): IPromise<Booking>;

  /**
   * @deprecated, prefer BookingFacade.close
   *
   * Close the response (contact)
   * @param {string} responseId the response identifier
   */
  close(responseId: string, isSuspend?: boolean, isApproval?: boolean): IPromise<Booking>;

  /**
   * @deprecated, prefer BookingFacade.refuse
   *
   * Refuse the response (contact)
   * @param {string} responseId the response identifier
   * @param {string} reason the reason of the rejection
   */
  refuse(responseId: string, reason: string): IPromise<Booking>;

  /**
   * @deprecated, prefer BookingFacade.defer
   *
   * Say to business that we defer the response with reply for increment metrics
   * @param {string} responseId the response identifier
   */
  deferWithResponse(responseId: string): IPromise<Booking>;

  /**
   * @deprecated, prefer BookingFacade.cancel
   *
   * Cancel the response (contact)
   * @param {string} responseId the response identifier
   */
  cancel(responseId: string): IPromise<Booking>;

  /**
   * @deprecated, prefer BookingFacade.cancel
   *
   * Cancel the response (contact)
   * @param {string} responseId the response identifier
   */
  cancelCampaign(responseId: string): IPromise<Booking>;

  /**
   * @deprecated, prefer BookingFacade.hangUp
   *
   * Hang up agent
   * @param {string} responseId the response identifier
   */
  hangUp(responseId: string): IPromise<Booking>;

  /**
   * @deprecated
   *
   * Returns an observable of contact state event
   */
  contactState$(): Observable<IContactStateEvent>;

  /**
   * @deprecated
   *
   * Returns an observable of contact updates
   */
  contactUpdated$(): Observable<Booking>;

  /**
   * @deprecated
   *
   * Returns an observable of selected contact
   */
  contactSelected$(): Observable<Booking>;

  /**
   * @deprecated
   *
   * Returns an observable of created chat contact
   */
  contactCreated$(): Observable<Booking>;
}

/**
 * @deprecated prefer BookingFacade
 */
export class ContactDeprecatedService implements IContactDeprecatedService {
  static $inject: Array<string> = [
    '$q',
    'contactStatusDeprecatedService',
    'contactService',
    'bookingMailService',
    'contactFacade',
    'ngrxActionService',
  ];

  public name = MODULE_NAME;

  private contacts$ = this.contactFacade.bookings$;
  private selected$ = this.contactFacade.selected$;
  private selectedQualif$ = this.contactFacade.selectedQualif$;
  private contacts: Booking[];
  private selected: Booking | undefined;
  private selectedQualif: Qualification | undefined;

  // listen store action to keep legacy behavior
  private _contactUpdated$ = this.actions$.pipe(
    ofType(BookingAction.Update),
    delay(100),
    mergeMap(({ id }) => of({}).pipe(withLatestFrom(this.contactFacade.getById(id), (_, contact) => contact)))
  );
  // selected contact event from store
  private _contactSelected$ = this.selected$.pipe(isNotNullOrUndefined());
  // success transition event from store
  private _contactState$ = this.contactFacade.success$.pipe(
    map(({ booking, current, previous }) => ({
      contact: booking,
      state: current,
      previousState: previous,
    }))
  );
  // created transition event from store
  private _contactCreated$ = this.contactFacade.created$.pipe(map(({ booking }) => booking));

  constructor(
    private readonly $q: IQService,
    private readonly contactStatusDeprecatedService: IContactStatusDeprecatedService,
    private readonly contactService: BookingService,
    private readonly bookingMailService: BookingMailService,
    private readonly contactFacade: BookingFacade,
    private readonly actions$: Actions
  ) {
    this.contacts$.subscribe(contacts => (this.contacts = contacts));
    this.selected$.subscribe(selected => (this.selected = selected));
    this.selectedQualif$.subscribe(selected => (this.selectedQualif = selected));
  }

  /**
   * @deprecated, prefer initialBooking constant
   */
  buildFakeContact(customerId: string): Booking {
    return {
      ...initialBooking,
      customerId,
    };
  }

  /**
   * @deprecated, prefer BookingFacade.getContact
   */
  getContact(responseId: string): IPromise<Booking> {
    return optToPromise(Option.of(this.contacts.find(c => c.responseId === responseId)), this.$q);
  }

  /**
   * @deprecated, prefer BookingFacade.getContact
   */
  getQualif(responseId: string): IPromise<Qualification | undefined> {
    const deferred = this.$q.defer<Qualification | undefined>();
    this.contactFacade
      .getQualifById(responseId)
      .toPromise()
      .then(
        qualification => deferred.resolve(qualification),
        () => deferred.reject()
      );
    return deferred.promise;
  }

  /**
   * @deprecated, prefer BookingFacade.getCustomer
   */
  getCustomer(responseId: string): IPromise<Customer | undefined> {
    return RxUtil.toPromise(this.contactFacade.getCustomerById(responseId), this.$q);
  }

  /**
   * @deprecated, prefer BookingFacade.getCustomerAddresses
   */
  getCustomerAddresses(responseId: string): IPromise<CustomerAddress[]> {
    return RxUtil.toPromise(this.contactFacade.getCustomerAddressesById(responseId), this.$q);
  }

  /**
   * @deprecated, prefer BookingFacade.contacts$
   */
  getContacts(): Booking[] {
    return this.contacts;
  }

  /**
   * @deprecated, prefer BookingFacade.contacts$
   */
  getContactsByChannel(c: Channel): Booking[] {
    return this.contacts.filter(({ channel }) => ChannelUtil.equals(c, channel));
  }

  /**
   * @deprecated, prefer BookingFacade.contacts$
   */
  getContactsByChannelType(type: ChannelType): Booking[] {
    return [ChannelDirection.INCOMING, ChannelDirection.OUTGOING, ChannelDirection.BOTH].reduce(
      (result, direction) => [...result, ...this.getContactsByChannel({ type, direction })],
      [] as Booking[]
    );
  }

  /**
   * @deprecated, prefer BookingFacade.contacts$
   */
  getChannelWithActiveContactsByChannelType(type: ChannelType): Channel[] {
    return [ChannelDirection.INCOMING, ChannelDirection.OUTGOING, ChannelDirection.BOTH].reduce((result, direction) => {
      let channel = { type, direction };
      return this.getContactsByChannel(channel).length > 0 ? [...result, channel] : result;
    }, [] as Channel[]);
  }

  /**
   * @deprecated, prefer BookingFacade.getTransitionById
   */
  getContactState(responseId: string): BookingStatus {
    return this.contactStatusDeprecatedService.getContactState(responseId);
  }

  /**
   * @deprecated, prefer BookingFacade.select
   */
  selectContact(responseId: string): void {
    this.contactFacade.select(responseId);
  }

  /**
   * @deprecated, prefer BookingFacade.selected$
   */
  getSelectedContact(): IPromise<Booking> {
    return optToPromise(Option.of(this.selected), this.$q);
  }

  /**
   * @deprecated, prefer BookingFacade.selectedQualif$
   */
  getSelectedQualif(): IPromise<Qualification | undefined> {
    return this.$q.resolve(this.selectedQualif);
  }

  /**
   * @deprecated, prefer BookingFacade.selectedId$
   */
  isContactSelected(responseId: string): boolean {
    return Option.of(this.selected)
      .filter(contact => contact.responseId === responseId)
      .isDefined();
  }

  /**
   * @deprecated, prefer BookingFacade.update
   */
  updateContext(responseId: string, newContext: { [key: string]: string }): IPromise<Booking> {
    this.contactFacade.update(responseId, {
      // deep copy to prevent immutability error
      context: deepCopy(newContext),
    });
    const ob$ = timer(100).pipe(mergeMap(() => this.contactFacade.getById(responseId).pipe(isNotNullOrUndefined())));
    return RxUtil.toPromise(ob$, this.$q);
  }

  /**
   * @deprecated, prefer BookingFacade.updateResponse
   */
  updateTransferredMailState(responseId: string, evt: IEpticaExctractEvent): IPromise<Booking> {
    this.contactFacade.update(responseId, {
      forward: {
        agentId: '',
        agentName: evt.description.actor.from,
        agentNickname: '',
        attachments: evt.attachment,
        previousSkills: Option.of(evt.skill)
          .flatMap(skill => Option.of(skill.source))
          .map(skills => skills.map(skill => skill.name))
          .getOrElse([]),
        comment: evt.comment || '',
      },
    });
    const ob$ = timer(100).pipe(mergeMap(() => this.contactFacade.getById(responseId).pipe(isNotNullOrUndefined())));
    return RxUtil.toPromise(ob$, this.$q);
  }

  /**
   * @deprecated, prefer BookingFacade.update
   */
  updateReschedule(
    responseId: string,
    reschedule: Reschedule,
    selectedDayTimeSlot: Option<TimeSlotSelect>,
    selectedTimeSlot: Option<TimeSlot>
  ): IPromise<Booking> {
    this.contactFacade.update(responseId, {
      // deep copy to prevent immutability error
      reschedule: deepCopy(reschedule),
      // deep copy to prevent immutability error
      selectedDayTimeSlot: selectedDayTimeSlot.map(slot => deepCopy(slot)).getOrUndefined(),
      // deep copy to prevent immutability error
      selectedTimeSlot: selectedTimeSlot.map(slot => deepCopy(slot)).getOrUndefined(),
    });
    const ob$ = timer(100).pipe(mergeMap(() => this.contactFacade.getById(responseId).pipe(isNotNullOrUndefined())));
    return RxUtil.toPromise(ob$, this.$q);
  }

  /**
   * @deprecated, prefer BookingFacade.update
   */
  updateRequestId(responseId: string, epticaRequestId: number): IPromise<Booking> {
    this.contactFacade.update(responseId, {
      epticaRequestId,
    });
    const ob$ = timer(100).pipe(mergeMap(() => this.contactFacade.getById(responseId).pipe(isNotNullOrUndefined())));
    return RxUtil.toPromise(ob$, this.$q);
  }

  /**
   * @deprecated, prefer BookingFacade.accept
   */
  accept(responseId: string): IPromise<Booking> {
    let ob$ = this.contactService.accept(responseId);
    return this.getContact(responseId).then(contact => RxUtil.toPromise(ob$, this.$q).then(() => contact));
  }

  /**
   * @deprecated, prefer BookingFacade.process
   */
  process(responseId: string): IPromise<Booking> {
    return this.getContact(responseId).then(contact =>
      RxUtil.toPromise(this.contactService.process(responseId, contact.connectionId, contact.agentConnectionId), this.$q).then(
        () => contact
      )
    );
  }

  /**
   * @deprecated, prefer BookingFacade.close
   */
  close(responseId: string, isSuspend: boolean = false, isApproval: boolean = false): IPromise<Booking> {
    let ob$ = this.contactService.close(responseId, isSuspend, isApproval);
    return this.getContact(responseId).then(contact => RxUtil.toPromise(ob$, this.$q).then(() => contact));
  }

  /**
   * @deprecated, prefer BookingFacade.refuse
   */
  refuse(responseId: string, reason: RefuseReason | string): IPromise<Booking> {
    let ob$ = this.contactService.refuse(responseId, reason);
    return this.getContact(responseId).then(contact => RxUtil.toPromise(ob$, this.$q).then(() => contact));
  }

  /**
   * @deprecated, prefer BookingFacade.deferWithResponse
   */
  deferWithResponse(responseId: string): IPromise<Booking> {
    let ob$ = this.bookingMailService.deferWithResponse(responseId);
    return this.getContact(responseId).then(contact => RxUtil.toPromise(ob$, this.$q).then(() => contact));
  }

  /**
   * @deprecated, prefer BookingFacade.hangUp
   */
  cancel(responseId: string): IPromise<Booking> {
    return this.refuse(responseId, 'TECH_FAILURE');
  }

  /**
   * @deprecated, prefer BookingFacade.hangUp
   */
  cancelCampaign(responseId: string): IPromise<Booking> {
    return this.refuse(responseId, 'CANCEL_CAMPAIGN_CONTACT');
  }

  /**
   * @deprecated, prefer BookingFacade.hangUp
   */
  hangUp(responseId: string): IPromise<Booking> {
    let ob$ = this.contactService.disconnect(responseId);
    return this.getContact(responseId).then(contact => RxUtil.toPromise(ob$, this.$q).then(() => contact));
  }

  /**
   * @deprecated
   */
  contactState$(): Observable<IContactStateEvent> {
    return this._contactState$;
  }

  /**
   * @deprecated
   */
  contactUpdated$(): Observable<Booking> {
    return this._contactUpdated$.pipe(isNotNullOrUndefined());
  }

  /**
   * @deprecated
   */
  contactSelected$(): Observable<Booking> {
    return this._contactSelected$;
  }

  /**
   * @deprecated
   */
  contactCreated$(): Observable<Booking> {
    return this._contactCreated$;
  }
}

export default MODULE_NAME;
